/**
 * purchaseSelector — Componente unico per la selezione d'acquisto corso.
 *
 * Fonde i due componenti storici (coursePage + purchaseDrawer) in un unico
 * factory con due modalità di inizializzazione:
 *
 *  - hydrate(course, pricingOptions): usata dalle SCHEDE (corso/global/package/
 *    bundle). I dati arrivano già pronti dal server (window.IKOS.course +
 *    $pricingOptions calcolati in PHP), quindi non servono fetch né ricalcoli.
 *    Dopo l'idratazione lancia refreshEnrollment() per aggiornare i dati
 *    volatili (posti/lista d'attesa) che il server potrebbe servire da cache.
 *
 *  - open(courseId, productType): usata dal DRAWER del catalogo. I dati corso
 *    vengono fetchati on-demand da /v1/courses/{id} e le opzioni prezzo sono
 *    costruite lato client (_buildPricingOptions), perché nel listing il
 *    selettore si apre solo per product_type === 'course'.
 *
 * La fonte dati è SEMPRE this.course (stato interno): nessun accesso diretto a
 * window.IKOS.course fuori da hydrate()/open().
 *
 * Uso nei template:
 *   Scheda:  <div x-data="purchaseSelector" x-init="hydrate(window.IKOS.course, window.IKOS.pricingOptions)">
 *   Drawer:  <div x-data="purchaseSelector" @open-purchase.window="open($event.detail.courseId, $event.detail.productType)">
 */
import { formatCredits } from '../utils/format.js';

export function registerPurchaseSelector(Alpine) {
    const factory = () => ({
        // ── Stato corso ─────────────────────────────────────
        courseId: null,
        course: null,
        pricingOptions: [],
        hydrated: false,

        // ── Drawer (solo listing) ───────────────────────────
        drawerOpen: false,
        drawerLoading: false,

        // ── Bottom-sheet + share + QR (solo scheda) ─────────
        sheetOpen: false,
        shareOpen: false,
        shareCopied: false,
        qrOpen: false,

        // ── Radio cards ─────────────────────────────────────
        selectedOption: 'with_credit',

        // ── Convenzione ─────────────────────────────────────
        selectedAgreement: '',
        agreementCode: '',
        agreementUser: '',
        agreementApplied: false,
        agreementError: '',
        agreementResult: null,

        // ── Codice sconto ───────────────────────────────────
        discountCode: '',
        discountEmail: '',
        discountApplied: false,
        discountError: '',
        discountResult: null,
        needsEmail: false,

        // ── Anteprima prezzo ────────────────────────────────
        originalPrice: 0,
        currentPrice: null,
        discountLabel: '',

        // ── UI ──────────────────────────────────────────────
        showDiscounts: false,
        loading: false,

        // ── Tracking (eventi ik:purchase letti da tracking/hooks.js) ──
        _sheetOpenedAt: 0,
        _lastOption: null,

        init() {
            // Reset stato pagamento + prezzo quando cambia l'opzione
            this.$watch('selectedOption', () => {
                this._resetPaymentState();
                this._updateOriginalPrice();
            });

            // Reset sotto-campi quando cambia la convenzione selezionata
            this.$watch('selectedAgreement', () => {
                this.agreementCode = '';
                this.agreementUser = '';
                this.agreementError = '';
            });
        },

        // ── Init modalità SCHEDA ────────────────────────────

        /**
         * Idrata il componente con i dati già renderizzati dal server.
         * @param {object} course         window.IKOS.course
         * @param {array}  pricingOptions  opzioni calcolate lato PHP
         */
        hydrate(course, pricingOptions) {
            if (!course || !course.id) return;

            this.course = course;
            this.courseId = course.id;
            // Normalizza le opzioni server-side: il tax_description può contenere
            // markup (es. "<small>(22%)</small>") che va ridotto a testo semplice,
            // come faceva il render PHP con strip_tags().
            this.pricingOptions = (Array.isArray(pricingOptions) ? pricingOptions : []).map(o => ({
                ...o,
                tax_description: this._stripTags(o.tax_description || ''),
            }));
            this.hydrated = true;

            // Default selection: prima l'opzione is_default, poi il fallback storico
            // (disabled_credit → without_credit) per robustezza.
            const defaultOpt = this.pricingOptions.find(o => o.is_default);
            if (defaultOpt) {
                this.selectedOption = defaultOpt.key;
            } else if (course.disabled_credit) {
                this.selectedOption = 'without_credit';
            }

            this._updateOriginalPrice();
            this._lastOption = this.selectedOption;

            // Aggiorna i dati volatili (posti/lista d'attesa/iscrizione) che il
            // server può servire da cache per gli utenti guest.
            this.refreshEnrollment();
        },

        /**
         * Rif​etcha enrollment_status/is_enrolled per superare la cache SSR.
         * Skippa gli utenti autenticati: per loro il server non cachea.
         */
        async refreshEnrollment() {
            if (!this.courseId) return;
            const authToken = window.IKOS?.authToken;
            if (authToken && authToken !== true) return; // loggato = dati già freschi

            try {
                const fresh = await this._fetchCourse(this.courseId);
                if (!fresh) return;
                // Sovrascrive SOLO i campi volatili, non i prezzi (SEO-stable).
                if (fresh.enrollment_status !== undefined) {
                    this.course.enrollment_status = fresh.enrollment_status;
                }
                if (fresh.is_enrolled !== undefined) {
                    this.course.is_enrolled = fresh.is_enrolled;
                }
                if (fresh.course_url !== undefined) {
                    this.course.course_url = fresh.course_url;
                }
            } catch (e) {
                // Silente: in caso di errore resta il dato SSR.
            }
        },

        // ── Init modalità DRAWER (listing) ──────────────────

        /**
         * Apre il drawer per un corso specifico dal catalogo.
         */
        async open(courseId, productType) {
            if (!courseId) return;

            // Prodotti non-corso noti dal listing: aggiunta diretta senza fetch
            if (productType && productType !== 'course') {
                try {
                    await Alpine.store('cart').addItem({ courses_id: courseId });
                } catch (err) {
                    window.dispatchEvent(new CustomEvent('cart-item-error', { detail: { message: this._getErrorMessage(err) } }));
                }
                return;
            }

            // Se è lo stesso corso già caricato, riapri senza fetch
            if (this.courseId === courseId && this.course) {
                this.drawerOpen = true;
                window.dispatchEvent(new CustomEvent('purchase-drawer-opened'));
                return;
            }

            this.courseId = courseId;
            this.course = null;
            this.pricingOptions = [];
            this.hydrated = false;
            this._resetPaymentState();

            try {
                const course = await this._fetchCourse(courseId);
                if (!course) throw new Error('fetch failed');

                // Fallback: se per qualche motivo non è un corso standard
                if (course.product_type !== 'course') {
                    try {
                        await Alpine.store('cart').addItem({ courses_id: courseId });
                    } catch (err) {
                        window.dispatchEvent(new CustomEvent('cart-item-error', { detail: { message: this._getErrorMessage(err) } }));
                    }
                    return;
                }

                // Corso standard: apri il drawer
                this.drawerOpen = true;
                this.drawerLoading = true;
                window.dispatchEvent(new CustomEvent('purchase-drawer-opened'));

                this.course = course;
                this.pricingOptions = this._buildPricingOptions(course);

                const defaultOpt = this.pricingOptions.find(o => o.is_default);
                if (defaultOpt) {
                    this.selectedOption = defaultOpt.key;
                }

                this._updateOriginalPrice();
                this._lastOption = this.selectedOption;
            } catch (e) {
                console.error('purchaseSelector: errore caricamento corso', e);
            } finally {
                this.drawerLoading = false;
            }
        },

        /**
         * Chiude il drawer del catalogo.
         */
        close() {
            this.drawerOpen = false;
        },

        // ── Bottom-sheet mobile (scheda) ────────────────────

        /**
         * @param {string} origin  da dove arriva il tap: 'sticky' (barra fissa
         *                         mobile), in futuro 'inline' o altro.
         */
        openSheet(origin = 'sticky') {
            this.sheetOpen = true;
            this._sheetOpenedAt = Date.now();
            this._emit('sheet_open', { cta_location: origin });
        },

        /**
         * @param {string} reason  'close' (X), 'backdrop', 'escape', 'add_to_cart'
         */
        closeSheet(reason = 'close') {
            if (!this.sheetOpen) return;
            this.sheetOpen = false;
            this._emit('sheet_close', {
                reason,
                seconds_open: this._sheetSeconds(),
                pricing_option: this.selectedOption,
            });
        },

        /**
         * Cambio dell'opzione prezzo da parte dell'utente (radio @change): il
         * $watch su selectedOption scatta anche durante hydrate()/open(), qui no.
         */
        onOptionChange(key) {
            const previous = this._lastOption;
            this._lastOption = key;
            if (previous === key) return;
            const opt = this.pricingOptions.find(o => o.key === key);
            this._emit('option_select', {
                option: key,
                previous_option: previous,
                price: opt ? opt.price : null,
                placement: this._placement(),
            });
        },

        toggleDiscounts() {
            this.showDiscounts = !this.showDiscounts;
            if (this.showDiscounts) {
                this._emit('discount_toggle_open', { placement: this._placement() });
            }
        },

        // ── Computed ────────────────────────────────────────

        get agreements() {
            return this.course?.agreements || [];
        },

        get agreementData() {
            if (!this.selectedAgreement) return null;
            return this.agreements.find(a => a.id == this.selectedAgreement) || null;
        },

        get hasMultipleOptions() {
            return this.pricingOptions.length > 1;
        },

        get isFad() {
            return (this.course?.type || '') === 'FAD';
        },

        // ── Payment: Convenzione ────────────────────────────

        async verifyAgreement() {
            if (!this.selectedAgreement) return;

            const agr = this.agreementData;

            if (agr?.requires_code && !this.agreementCode.trim()) {
                this.agreementError = 'Inserisci il codice della convenzione';
                return;
            }
            if (agr?.requires_user && !this.agreementUser.trim()) {
                this.agreementError = agr.user_field_required === 'fiscal_code'
                    ? 'Inserisci il codice fiscale'
                    : (agr.user_field_required === 'email'
                        ? 'Inserisci l\'email'
                        : 'Inserisci codice fiscale o email');
                return;
            }

            this.loading = true;
            this.agreementError = '';

            try {
                const body = {
                    agreement_id: parseInt(this.selectedAgreement, 10),
                    course_id: this.courseId,
                };
                if (agr?.requires_code) body.code = this.agreementCode.trim();
                if (agr?.requires_user) body.user = this.agreementUser.trim();

                const result = await this._apiCall('/v1/agreements/validate', body);

                this.agreementApplied = true;
                this.agreementResult = result;
                this.currentPrice = result.final_price;
                this.discountLabel = '-' + result.discount_value + '%';
                this._emit('discount_apply', { code_type: 'agreement', result: 'ok', discount_value: result.discount_value ?? null, placement: this._placement() });
            } catch (err) {
                this.agreementError = this._getErrorMessage(err);
                this._emit('discount_apply', { code_type: 'agreement', result: this._errorKey(err), placement: this._placement() });
            } finally {
                this.loading = false;
            }
        },

        removeAgreement() {
            this.selectedAgreement = '';
            this.agreementCode = '';
            this.agreementUser = '';
            this.agreementApplied = false;
            this.agreementError = '';
            this.agreementResult = null;
            this.currentPrice = null;
            this.discountLabel = '';
        },

        // ── Payment: Codice sconto ──────────────────────────

        async validateCode() {
            if (!this.discountCode.trim()) return;

            this.loading = true;
            this.discountError = '';

            try {
                const body = {
                    code: this.discountCode.trim(),
                    course_id: this.courseId,
                };
                if (this.discountEmail) body.email = this.discountEmail.trim();

                const result = await this._apiCall('/v1/codes/validate', body);

                if (result.requires_email && !result.valid) {
                    this.needsEmail = true;
                    this._emit('discount_apply', { code_type: 'coupon', result: 'needs_email', placement: this._placement() });
                    return;
                }

                this.discountApplied = true;
                this.discountResult = result;
                this.needsEmail = false;
                this._emit('discount_apply', {
                    code_type: result.type || 'coupon',
                    result: 'ok',
                    discount_value: result.discount_value ?? result.discount_amount ?? null,
                    placement: this._placement(),
                });

                if (result.type === 'coupon' && result.final_price !== undefined) {
                    this.currentPrice = result.final_price;
                    if (result.discount_type === 'percentage') {
                        this.discountLabel = '-' + result.discount_value + '%';
                    } else if (result.discount_amount) {
                        this.discountLabel = '-' + this.formatPrice(result.discount_amount);
                    }
                }
            } catch (err) {
                this.discountError = this._getErrorMessage(err);
                this.needsEmail = false;
                this._emit('discount_apply', { code_type: 'coupon', result: this._errorKey(err), placement: this._placement() });
            } finally {
                this.loading = false;
            }
        },

        removeCode() {
            this.discountCode = '';
            this.discountEmail = '';
            this.discountApplied = false;
            this.discountError = '';
            this.discountResult = null;
            this.needsEmail = false;
            this.currentPrice = null;
            this.discountLabel = '';
        },

        // ── Cart ────────────────────────────────────────────

        async addToCart() {
            if (!this.courseId) return;

            // Posti esauriti: blocca l'aggiunta e mostra un messaggio.
            // La lista d'attesa resta disponibile tramite il pulsante dedicato.
            // TODO: consentire l'eccezione per chi presenta un coupon dedicato al corso.
            const availableSpots = this.course?.enrollment_status?.available_spots;
            if (availableSpots != null && Number(availableSpots) <= 0) {
                window.dispatchEvent(new CustomEvent('cart-item-error', {
                    detail: { message: 'I posti per questo corso sono esauriti: non è più possibile iscriversi.' },
                }));
                return;
            }

            // Auto-applica codice sconto se compilato ma non ancora validato
            if (this.discountCode.trim() && !this.discountApplied) {
                await this.validateCode();
                if (!this.discountApplied) return;
            }

            // Auto-applica convenzione se selezionata ma non ancora verificata
            if (this.selectedAgreement && !this.agreementApplied) {
                await this.verifyAgreement();
                if (!this.agreementApplied) return;
            }

            this.loading = true;

            try {
                const body = { courses_id: this.courseId };

                // Opzione senza crediti
                if (this.selectedOption === 'without_credit') {
                    body.disabled_credit = 1;
                }

                // Convenzione
                if (this.agreementApplied && this.selectedAgreement) {
                    body.agreements_id = parseInt(this.selectedAgreement, 10);
                    if (this.agreementCode) body.agreement_code = this.agreementCode.trim();
                    if (this.agreementUser) body.agreement_user = this.agreementUser.trim();
                }

                // Codice sconto: routing per tipo
                if (this.discountApplied && this.discountCode && this.discountResult) {
                    const type = this.discountResult.type;
                    if (type === 'coupon') {
                        body.coupon = this.discountCode.trim();
                    } else if (type === 'automatic_discount') {
                        body.sconto_codice_auto = {
                            code: this.discountResult.code,
                            email: this.discountEmail.trim(),
                            discount: this.discountResult.discount_value,
                        };
                    }
                    // type === 'promotion': non va nel POST, si applica via PATCH dopo
                }

                // Contesto per l'evento add_to_cart (letto e azzerato da
                // tracking/hooks.js): da dove si è aggiunto e con quale opzione.
                // Nome e categoria dal corso idratato: la risposta del carrello
                // porta il titolo breve, view_item usa il titolo completo.
                Alpine.store('cart')._trackingContext = {
                    cta_location:       this._placement(),
                    pricing_option:     this.selectedOption,
                    sheet_open_seconds: this.sheetOpen ? this._sheetSeconds() : undefined,
                    discount_type:      this.agreementApplied ? 'agreement'
                                        : (this.discountApplied ? (this.discountResult?.type || 'coupon') : undefined),
                    item_name:          this.course?.title || undefined,
                    item_category:      this.course?.category || this.course?.type || undefined,
                };

                // Chiudi i contenitori aperti prima di procedere, per evitare
                // conflitti con x-trap.noscroll (bottom-sheet scheda / drawer listing).
                if (this.sheetOpen) this.closeSheet('add_to_cart');
                if (this.drawerOpen) this.close();
                await this.$nextTick();

                // Delega allo store: aggiorna stato, apre cart drawer
                await Alpine.store('cart').addItem(body);

                // Promozione: applica via PATCH dopo aver aggiunto il corso
                if (this.discountApplied && this.discountResult?.type === 'promotion') {
                    await Alpine.store('cart').applyPromotion(this.discountCode.trim());
                }
            } catch (err) {
                window.dispatchEvent(new CustomEvent('cart-item-error', { detail: { message: this._getErrorMessage(err) } }));
            } finally {
                this.loading = false;
            }
        },

        // ── Share (scheda) ──────────────────────────────────

        async shareCourse() {
            const url = window.location.href;
            const title = this.course?.title || document.title;
            const isMobile = window.matchMedia('(hover: none)').matches;

            if (isMobile && navigator.share) {
                try {
                    await navigator.share({ title, url });
                } catch { /* utente ha annullato */ }
            } else {
                this.shareCopied = false;
                this.shareOpen = true;
            }
        },

        async copyShareUrl() {
            try {
                await navigator.clipboard.writeText(window.location.href);
            } catch {
                this.$refs.shareInput?.select();
                document.execCommand('copy');
            }
            this.shareCopied = true;
            setTimeout(() => { this.shareCopied = false; }, 2500);
        },

        // ── Formatting ──────────────────────────────────────

        formatPrice(value) {
            if (value == null) return '';
            return parseFloat(value).toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }) + ' €';
        },

        formatDisplayPrice(value) {
            if (value == null) return '';
            return parseFloat(value).toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }) + ' €';
        },

        formatLearningHours(course) {
            const hours = parseInt(course.learning_hours || 0);
            const lessons = parseInt(course.count_lessons || 0);
            let text = hours + ' ore';
            if (lessons > 0) text += ' strutturate in ' + lessons + ' lezioni';
            return text;
        },

        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
        },

        formatDateRange(startAt, endAt) {
            const start = this.formatDate(startAt);
            if (!endAt || endAt === startAt) return start;
            return start + ' - ' + this.formatDate(endAt);
        },

        /**
         * True se la data è successiva a oggi (usato per label "Disponibile dal"
         * vs "Data" e per lo stile error dei corsi non ancora disponibili).
         */
        isFutureDate(dateStr) {
            if (!dateStr) return false;
            const d = new Date(dateStr);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return d > today;
        },

        // ── Helpers privati ─────────────────────────────────

        /**
         * Fetch corso da API con gli header di autenticazione correnti.
         */
        async _fetchCourse(courseId) {
            const apiBase = window.IKOS?.apiBase || '';
            const fetchHeaders = { 'Accept': 'application/json' };
            const authToken = window.IKOS?.authToken;
            if (authToken && authToken !== true) {
                fetchHeaders['Auth-Token'] = authToken;
            }
            const res = await fetch(`${apiBase}/v1/courses/${courseId}`, { headers: fetchHeaders });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
        },

        /**
         * Port JS di CourseController::buildPricingOptions().
         * Usato SOLO in modalità drawer (product_type === 'course'): le schede
         * ricevono pricingOptions già calcolati dal PHP via hydrate().
         */
        _buildPricingOptions(course) {
            const options = [];
            const subOpts = course.subscription_options || [];
            const hasCredits = parseFloat(course.credit || 0) > 0;
            const disabledCredit = !!course.disabled_credit;
            const disabledReasons = course.disabled_credit_reasons || [];
            const noDisciplina = disabledReasons.includes('no_disciplina');

            // Caso: crediti disabilitati per professione non compatibile
            if (disabledCredit && noDisciplina && hasCredits) {
                options.push({
                    key: 'with_credit',
                    label: 'Con ' + formatCredits(course.credit) + ' crediti ECM',
                    price: parseFloat(course.credit_price || course.price || 0),
                    tax_description: this._stripTags(course.credit_tax_description || ''),
                    is_default: false,
                    disabled: true,
                    disabled_reason: 'no_disciplina',
                    has_agreements: false,
                    has_coupon: false,
                });
                options.push({
                    key: 'without_credit',
                    label: 'Senza crediti ECM',
                    price: parseFloat(course.price || 0),
                    tax_description: this._stripTags(course.tax_description || ''),
                    is_default: true,
                    disabled: false,
                    has_agreements: subOpts.includes('agreement'),
                    has_coupon: subOpts.includes('coupon'),
                });
                return options;
            }

            // Caso normale: opzione principale
            options.push({
                key: 'with_credit',
                label: hasCredits ? 'Con ' + formatCredits(course.credit) + ' crediti ECM' : 'Acquista il corso',
                price: parseFloat(course.price || 0),
                tax_description: this._stripTags(course.tax_description || ''),
                is_default: true,
                disabled: false,
                has_agreements: subOpts.includes('agreement'),
                has_coupon: subOpts.includes('coupon'),
            });

            // Opzione senza crediti (se disponibile)
            if (subOpts.includes('credit_disabled') && course.disabled_credit_price) {
                options.push({
                    key: 'without_credit',
                    label: 'Senza crediti ECM',
                    price: parseFloat(course.disabled_credit_price),
                    tax_description: this._stripTags(course.disabled_credit_tax_description || ''),
                    is_default: false,
                    disabled: false,
                    has_agreements: subOpts.includes('agreement'),
                    has_coupon: subOpts.includes('coupon'),
                });
            }

            return options;
        },

        _resetPaymentState() {
            this.removeAgreement();
            this.removeCode();
            this.showDiscounts = false;
        },

        _updateOriginalPrice() {
            if (!this.course) return;
            const opt = this.pricingOptions.find(o => o.key === this.selectedOption);
            this.originalPrice = opt ? opt.price : parseFloat(this.course.price || 0);
        },

        async _apiCall(endpoint, body) {
            const apiBase = window.IKOS?.apiBase || '';
            let res;

            try {
                res = await fetch(`${apiBase}${endpoint}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify(body),
                });
            } catch (e) {
                throw { _network: true };
            }

            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw data;
            return data;
        },

        /**
         * Dove sta il selettore in questo momento: bottom sheet mobile della
         * scheda, drawer del catalogo o sidebar desktop della scheda.
         */
        _placement() {
            if (this.sheetOpen) return 'sheet';
            if (this.drawerOpen) return 'catalog_drawer';
            return 'sidebar';
        },

        _sheetSeconds() {
            return this._sheetOpenedAt ? Math.round((Date.now() - this._sheetOpenedAt) / 1000) : 0;
        },

        /**
         * Chiave dell'errore API (es. "notValid", "usageLimitReached") per il
         * tracking: il messaggio tradotto non serve a GA4, la causa sì.
         */
        _errorKey(err) {
            if (err?._network) return 'network';
            const messages = err?.validation_messages || {};
            for (const field of Object.values(messages)) {
                if (field && typeof field === 'object') {
                    const key = Object.keys(field)[0];
                    if (key) return key;
                }
            }
            return 'invalid';
        },

        /**
         * Evento di tracking del selettore. Letto da tracking/hooks.js, che lo
         * traduce nell'evento GA4 e aggiunge il contesto di pagina.
         */
        _emit(type, detail = {}) {
            try {
                window.dispatchEvent(new CustomEvent('ik:purchase', {
                    detail: {
                        course_id:   this.courseId,
                        course_code: this.course?.code,
                        course_type: this.course?.category || this.course?.type,
                        ...detail,
                        type, // tipo dell'evento: vince su qualsiasi campo omonimo del detail
                    },
                }));
            } catch (e) { /* tracking silenzioso */ }
        },

        _stripTags(html) {
            if (!html) return '';
            const tmp = document.createElement('div');
            tmp.innerHTML = html;
            return tmp.textContent || tmp.innerText || '';
        },

        _getErrorMessage(err) {
            if (err?._network) return 'Errore di connessione';

            const translations = {
                // cart
                notFound: 'Corso non trovato',
                notAvailable: 'Questo corso non è al momento disponibile per l\'acquisto',
                alreadySubscribed: 'Sei già iscritto a questo corso',
                alreadyOwned: 'Hai già una Global Card attiva per quest\'anno',
                bundleCourseAlreadyEnrolled: 'Sei già iscritto a uno dei corsi inclusi in questo pacchetto',
                // codes-validate
                notValid: 'Codice non valido',
                alreadyUsed: 'Questo coupon è già stato utilizzato',
                maxCredits: 'Il coupon ha raggiunto il limite massimo di crediti ECM utilizzabili',
                maxValue: 'Il coupon ha raggiunto il limite massimo di valore utilizzabile',
                birthday_required: 'Accedi per utilizzare questo codice',
                invalid_birth_date: 'Codice valido solo in prossimità del tuo compleanno',
                already_used: 'Hai già utilizzato questo codice',
                excluded_course: 'Il codice non è valido per questo corso',
                // agreements-validate
                usageLimitReached: 'La convenzione ha raggiunto il limite di utilizzo',
                creditLimitReached: 'La convenzione ha raggiunto il limite di crediti',
                valueLimitReached: 'La convenzione ha raggiunto il limite di valore',
            };

            const messages = err?.validation_messages || {};
            for (const field of Object.values(messages)) {
                if (typeof field === 'object') {
                    for (const key of Object.keys(field)) {
                        if (translations[key]) return translations[key];
                    }
                }
            }

            return err?.detail || 'Si è verificato un errore';
        },
    });

    Alpine.data('purchaseSelector', factory);
}
